import React, { useCallback, useState } from 'react';
import { Stack, Button } from '@mui/material';
import { CampaignStatus } from '../../types';
import { SaveMode } from '../../types';
import { useMessageValidity } from '../../context';

interface ActionsProps {
  onSubmit: (
    status: CampaignStatus,
    mode: SaveMode,
    activate?: boolean,
  ) => Promise<unknown> | unknown;
  /** Editing an existing campaign (campaignId present and not a duplicate). */
  isEdit: boolean;
  /** Status of the campaign being edited, if any. */
  campaignStatus?: CampaignStatus;
  /** New campaign or duplicate flow. */
  isNewOrDuplicate: boolean;
  /** Form has unsaved changes. */
  isDirty: boolean;
  /** Disable the primary (Create/Update/Activate) button — per-policy validity. */
  primaryDisabled?: boolean;
  /** Disable the Save as Draft button — disabled when the name is empty. */
  draftDisabled?: boolean;
  /** A save is running, whether started here or from one of the builder's dialogs. */
  saving?: boolean;
}

/**
 * Tracks a save started by a button, including the pre-save checks that run before the form
 * itself reports saving. The api client already toasts errors; swallowing the rejection keeps it
 * from escaping the click handler and leaves the form open with the user's edits.
 */
export const useRunSave = () => {
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (save: () => Promise<unknown> | unknown) => {
    setBusy(true);
    try {
      await save();
    } catch (error) {
      console.error('Campaign save failed', error);
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, run };
};

export const Actions: React.FC<ActionsProps> = ({
  onSubmit,
  isEdit,
  campaignStatus,
  isNewOrDuplicate,
  isDirty,
  primaryDisabled,
  draftDisabled,
  saving = false,
}) => {
  const { busy, run: runSave } = useRunSave();
  const run = (status: CampaignStatus, activate: boolean) =>
    runSave(() => onSubmit(status, isEdit ? 'update' : 'create', activate));

  const isDraftCampaign = campaignStatus === 'draft';
  const shouldShowDraftButton = isDraftCampaign || isNewOrDuplicate || isDirty;
  const primaryLabel = isDraftCampaign && isEdit ? 'Activate' : isEdit ? 'Update' : 'Create';
  // Draft-button visibility is a layout decision, not activation intent. In
  // particular, making an inactive campaign dirty reveals Save as Draft but its
  // Update action must still preserve the inactive status.
  const shouldActivatePrimary = isDraftCampaign || isNewOrDuplicate;
  // An existing draft remains a draft in the save payload. The dedicated
  // activation endpoint is the operation that transitions it to active, which
  // keeps a failed activation from leaving the persisted status half-changed.
  const primaryStatus: CampaignStatus = isDraftCampaign
    ? 'draft'
    : isNewOrDuplicate
      ? 'active'
      : campaignStatus ?? 'active';

  return (
    <Stack
      direction="row"
      spacing={0}
      gap="10px"
      justifyContent="flex-end"
      alignItems="center"
      width="100%"
    >
      {shouldShowDraftButton && (
        <Button
          variant="outlined"
          disabled={busy || saving || draftDisabled}
          style={{ width: '150px', marginTop: '20px' }}
          onClick={() => run('draft', false)}
        >
          Save as Draft
        </Button>
      )}
      <Button
        variant="contained"
        disabled={busy || saving || primaryDisabled}
        style={{ width: '150px', marginTop: '20px' }}
        onClick={() => run(primaryStatus, shouldActivatePrimary)}
      >
        {primaryLabel}
      </Button>
    </Stack>
  );
};

/** Send-limit errors block activation/update, but old drafts could still be saved. */
export const PrimaryActions: React.FC<React.ComponentProps<typeof Actions>> = (props) => {
  const { hasInvalid } = useMessageValidity();
  // draftDisabled deliberately not re-passed: `{...props}` already carries it,
  // and a send-limit error must not block parking the campaign as a draft.
  return <Actions {...props} primaryDisabled={props.primaryDisabled || hasInvalid} />;
};
